import type { Row } from "@tanstack/react-table"
import { useFieldArray } from "react-hook-form"
import { useShallow } from "zustand/shallow"

import { useCalculatePurchaseOrderPricing } from "../../hooks/useCalculatePurchaseOrderPricing"
import usePurchaseOrderForm from "../../hooks/usePurchaseOrderForm"
import { transformQuantityField, updateLineValue } from "../utils"

import { type OrderLine, type UnitOfMeasurement } from "@/graphql/codegen/graphql"
import {
  useClearOrderLineRounded,
  useSetOrderLineRounded,
  useSetPricingFetching,
} from "@/screens/Procurement/PurchaseOrder/contexts/PurchaseOrderContext"
import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore"
import { createUomsList } from "@/utils/conversion"
import {
  getWeightPerPallet,
  hasFloatPallets,
  isBagsWholeNumber,
  mapCalculatedPricingParams,
} from "@/utils/purchaseOrder"
import useDebounce from "@/utils/useDebounce"

interface UseOrderQuantityColumnProps {
  uomsDropdownList: UnitOfMeasurement[]
  handleInputChange: (args: { value: string; name: string }) => void
  getLineValue: (fieldName: keyof OrderLine) => any
}

export const useOrderQuantityColumn = ({ row }: { row: Row<OrderLine> }): UseOrderQuantityColumnProps => {
  const { setField, methods, formValues: order } = usePurchaseOrderForm()

  const { fields: orderLines } = useFieldArray({
    control: methods.control,
    name: "orderLines",
  })
  const setOrderLineRounded = useSetOrderLineRounded()
  const clearOrderLineRounded = useClearOrderLineRounded()

  const { getCalculatePurchaseOrderPricing } = useCalculatePurchaseOrderPricing()
  const getCalculatePurchaseOrderPricingDebounced = useDebounce(getCalculatePurchaseOrderPricing, 300)
  const setPricingFetching = useSetPricingFetching()

  const { uomId, product } = row.original

  const weightPerPallet = getWeightPerPallet(product!, uomId)
  const units = useUnitOfMeasurementsStore(useShallow((s) => s.units))
  const uomsDropdownList = createUomsList(units, product)

  const setLineValue = (values: Partial<OrderLine>) => {
    const updatedLines = orderLines.map((line, index) => {
      if (index === row.index) {
        return {
          ...line,
          ...values,
        }
      }
      return line
    })

    setField("orderLines", updatedLines, { shouldDirty: true })
  }

  const getLineValue = <T>(fieldName: keyof OrderLine): T | null => {
    const currentLine = orderLines[row.index]

    if (!currentLine) {
      return null
    }

    return (currentLine as any)[fieldName] as T
  }

  const handleInputChange = ({ value, name }: { value: string; name: string }) => {
    // Handles the case where weight per pallet is non-zero
    const handleNonZeroWeight = () => {
      const currentQuantity = getLineValue<number>("quantityInUom") || 0
      const currentUomId = getLineValue<string>("uomId")

      if (!currentUomId || !product) {
        return
      }

      return transformQuantityField({
        currentQuantity,
        currentUomId,
        name,
        product,
        value,
      })
    }

    let formattedValue: number | null = null

    if (weightPerPallet === 0) {
      const newLineValue = updateLineValue({ name, value })

      if (newLineValue) {
        setLineValue(newLineValue)
      }
    } else {
      setPricingFetching(true)
      const updatedValues = handleNonZeroWeight()
      if (updatedValues) {
        const uomId = updatedValues.uomId ?? getLineValue<string>("uomId") ?? ""
        setLineValue({
          numberOfPallets: updatedValues.numberOfPallets || 0,
          quantityInUom: updatedValues.quantityInUom || 0,
          uomId,
        })

        const hasFloat = hasFloatPallets(updatedValues.numberOfPallets!)

        if (hasFloat) {
          setOrderLineRounded(row.index, isBagsWholeNumber(product!, updatedValues.quantityInUom || 0, uomId))
        } else {
          clearOrderLineRounded(row.index)
        }

        formattedValue = updatedValues.quantityInUom || null
      }

      const calculatedPricingParams = mapCalculatedPricingParams({
        orderForm: order,
        product: product!,
        quantity: formattedValue,
        updatedUomId: updatedValues?.uomId,
      })

      getCalculatePurchaseOrderPricingDebounced(calculatedPricingParams)
    }
  }

  return {
    uomsDropdownList,
    handleInputChange,
    getLineValue,
  }
}
